// src/context/AlertsContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { CountyContext } from "../components/CountyContext";

const AlertsContext = createContext();

// Higher number = show earlier within the same tier (used in NwsAlerts)
const getSubtypeSeverityRank = (feature) => {
  const props = feature && feature.properties ? feature.properties : {};
  const rawEvent = (props.event || "").toLowerCase();
  const rawHeadline = (props.headline || "").toLowerCase();
  const txt = rawEvent + " " + rawHeadline;

  const isWinterStorm =
    txt.indexOf("winter storm") !== -1 ||
    txt.indexOf("winter weather") !== -1 ||
    txt.indexOf("blizzard") !== -1 ||
    txt.indexOf("snow squall") !== -1;

  const isExtremeCold =
    txt.indexOf("extreme cold") !== -1 ||
    txt.indexOf("wind chill") !== -1 ||
    txt.indexOf("cold weather advisory") !== -1;

  if (isWinterStorm) return 2;
  if (isExtremeCold) return 1;
  return 0;
};

export const AlertsProvider = ({ children, countyMapping }) => {
  const { selectedCounty } = useContext(CountyContext);

  const [alerts, setAlerts] = useState([]);
  const [wildfireAlerts, setWildfireAlerts] = useState([]); // NEW
  const [isLoading, setIsLoading] = useState(false);

  const [flags, setFlags] = useState({
    hasBurnBan: false,
    hasWinterStorm: false,
    hasWinterWatch: false,
    hasWinterWarning: false,
    hasSevereStorm: false,
    hasFlooding: false,
    hasFog: false,
    hasFireWeather: false,
    hasHighWinds: false,
    hasExtremeCold: false,
    hasSurfHazard: false,
  });

  const [flagsReady, setFlagsReady] = useState(false);

  // Track the latest county in a ref to avoid stale-closure comparisons
  const countyRef = useRef(selectedCounty);
  useEffect(() => {
    countyRef.current = selectedCounty;
  }, [selectedCounty]);

  const resetFlags = () => {
    setFlags({
      hasBurnBan: false,
      hasWinterStorm: false,
      hasWinterWatch: false,
      hasWinterWarning: false,
      hasSevereStorm: false,
      hasFlooding: false,
      hasFog: false,
      hasFireWeather: false,
      hasHighWinds: false,
      hasExtremeCold: false,
      hasSurfHazard: false,
    });
    setFlagsReady(false);
  };

  const updateDerivedFlags = (nextAlerts) => {
    let hasBurnBan = false;
    let hasWinterStorm = false;
    let hasWinterWatch = false;
    let hasWinterWarning = false;
    let hasSevereStorm = false;
    let hasFlooding = false;
    let hasFog = false;
    let hasFireWeather = false;
    let hasHighWinds = false;
    let hasExtremeCold = false;
    let hasSurfHazard = false;

    nextAlerts.forEach((a) => {
      if (a.type === "burnBan") {
        hasBurnBan = true;
        return;
      }

      const props = a.raw && a.raw.properties;
      if (!props) return;

      const rawEvent = props.event || "";
      const rawHeadline = props.headline || "";
      const rawDesc = props.description || "";

      const event = rawEvent.toLowerCase();
      const headline = rawHeadline.toLowerCase();
      const desc = rawDesc.toLowerCase();
      const text = headline + " " + desc;

      // WINTER WARNING TITLES
      const isWinterWarningTitle =
        rawEvent.indexOf("Winter Storm Warning") !== -1 ||
        rawEvent.indexOf("Blizzard Warning") !== -1 ||
        rawEvent.indexOf("Ice Storm Warning") !== -1 ||
        rawEvent.indexOf("Snow Squall Warning") !== -1 ||
        rawHeadline.indexOf("Winter Storm Warning") !== -1 ||
        rawHeadline.indexOf("Blizzard Warning") !== -1 ||
        rawHeadline.indexOf("Ice Storm Warning") !== -1 ||
        rawHeadline.indexOf("Snow Squall Warning") !== -1;

      const isWinterWatchTitle =
        rawEvent.indexOf("Winter Storm Watch") !== -1 ||
        rawEvent.indexOf("Winter Weather Advisory") !== -1 ||
        rawEvent.indexOf("Blizzard Watch") !== -1 ||
        rawEvent.indexOf("Freezing Rain Advisory") !== -1 ||
        rawEvent.indexOf("Freezing Drizzle Advisory") !== -1 ||
        rawHeadline.indexOf("Winter Storm Watch") !== -1 ||
        rawHeadline.indexOf("Winter Weather Advisory") !== -1 ||
        rawHeadline.indexOf("Blizzard Watch") !== -1 ||
        rawHeadline.indexOf("Freezing Rain Advisory") !== -1 ||
        rawHeadline.indexOf("Freezing Drizzle Advisory") !== -1;

      if (isWinterWarningTitle) {
        hasWinterStorm = true;
        hasWinterWarning = true;
      }

      if (isWinterWatchTitle) {
        hasWinterStorm = true;
        if (!hasWinterWarning) {
          hasWinterWatch = true;
        }
      }

      // SURF / RIP TITLE
      const isSurfTitle =
        rawEvent.indexOf("High Surf") !== -1 ||
        rawEvent.indexOf("Rip Current") !== -1 ||
        rawEvent.indexOf("Beach Hazards") !== -1 ||
        rawHeadline.indexOf("High Surf") !== -1 ||
        rawHeadline.indexOf("Rip Current") !== -1 ||
        rawHeadline.indexOf("Beach Hazards") !== -1;

      if (isSurfTitle) {
        hasSurfHazard = true;
      }

      // COLD TITLE
      const isColdTitle =
        rawEvent.indexOf("Freeze") !== -1 ||
        rawEvent.indexOf("Frost") !== -1 ||
        rawEvent.indexOf("Wind Chill") !== -1 ||
        rawEvent.indexOf("Extreme Cold") !== -1 ||
        rawEvent.indexOf("Cold Weather Advisory") !== -1 ||
        rawHeadline.indexOf("Freeze") !== -1 ||
        rawHeadline.indexOf("Frost") !== -1 ||
        rawHeadline.indexOf("Wind Chill") !== -1 ||
        rawHeadline.indexOf("Extreme Cold") !== -1 ||
        rawHeadline.indexOf("Cold Weather Advisory") !== -1;

      if (isColdTitle) {
        hasExtremeCold = true;
      }

      // SEVERE STORMS TITLE
      const isSevereTitle =
        rawEvent.indexOf("Severe Thunderstorm") !== -1 ||
        rawEvent.indexOf("Tornado") !== -1 ||
        rawEvent.indexOf("Flash Flood") !== -1 ||
        rawHeadline.indexOf("Severe Thunderstorm") !== -1 ||
        rawHeadline.indexOf("Tornado") !== -1 ||
        rawHeadline.indexOf("Flash Flood") !== -1;

      if (isSevereTitle) {
        hasSevereStorm = true;
      }

      // FLOOD TITLE
      const isFloodTitle =
        rawEvent.indexOf("Flood") !== -1 ||
        rawHeadline.indexOf("Flood") !== -1;

      if (isFloodTitle) {
        hasFlooding = true;
      }

      // FOG TITLE
      const isFogTitle =
        rawEvent.indexOf("Dense Fog") !== -1 ||
        rawEvent.indexOf("Fog Advisory") !== -1 ||
        rawHeadline.indexOf("Dense Fog") !== -1 ||
        rawHeadline.indexOf("Fog Advisory") !== -1;

      if (isFogTitle) {
        hasFog = true;
      }

      // FIRE WEATHER TITLE
      const isFireTitle =
        rawEvent.indexOf("Red Flag") !== -1 ||
        rawEvent.indexOf("Fire Weather") !== -1 ||
        rawHeadline.indexOf("Red Flag") !== -1 ||
        rawHeadline.indexOf("Fire Weather") !== -1;

      if (isFireTitle) {
        hasFireWeather = true;
        hasBurnBan = false;
      }
      if (
        text.indexOf("red flag conditions") !== -1 ||
        text.indexOf("critical fire weather") !== -1 ||
        text.indexOf("wildfire") !== -1
      ) {
        hasFireWeather = true;
        hasBurnBan = false;
      }

      // WIND TITLE
      const isWindTitle =
        rawEvent.indexOf("High Wind") !== -1 ||
        rawEvent.indexOf("Wind Advisory") !== -1 ||
        rawHeadline.indexOf("High Wind") !== -1 ||
        rawHeadline.indexOf("Wind Advisory") !== -1;

      if (isWindTitle) {
        hasHighWinds = true;
      }

      // DESCRIPTION-BASED

      if (
        event.indexOf("winter storm") !== -1 ||
        event.indexOf("blizzard") !== -1 ||
        event.indexOf("winter weather advisory") !== -1 ||
        text.indexOf("snow") !== -1 ||
        text.indexOf("freezing rain") !== -1 ||
        text.indexOf("freezing drizzle") !== -1 ||
        text.indexOf("sleet") !== -1
      ) {
        hasWinterStorm = true;
      }

      if (event.indexOf("special weather statement") !== -1) {
        if (
          text.indexOf("snow") !== -1 ||
          text.indexOf("freezing rain") !== -1 ||
          text.indexOf("freezing drizzle") !== -1 ||
          text.indexOf("sleet") !== -1
        ) {
          hasWinterStorm = true;
          if (!hasWinterWarning) {
            hasWinterWatch = true;
          }
        }
      }

      if (
        text.indexOf("high surf") !== -1 ||
        text.indexOf("rip current") !== -1
      ) {
        hasSurfHazard = true;
      }

      if (
        text.indexOf("freeze") !== -1 ||
        text.indexOf("frost") !== -1 ||
        text.indexOf("dangerously cold") !== -1 ||
        text.indexOf("bitterly cold") !== -1 ||
        text.indexOf("wind chill") !== -1
      ) {
        hasExtremeCold = true;
      }

      if (
        text.indexOf("flash flooding") !== -1 ||
        text.indexOf("flash flood") !== -1 ||
        text.indexOf("heavy rain") !== -1
      ) {
        hasFlooding = true;
      }

      if (
        text.indexOf("dense fog") !== -1 ||
        text.indexOf("fog") !== -1
      ) {
        hasFog = true;
      }

      if (
        text.indexOf("red flag conditions") !== -1 ||
        text.indexOf("critical fire weather") !== -1 ||
        text.indexOf("wildfire") !== -1
      ) {
        hasFireWeather = true;
      }

      if (
        text.indexOf("strong winds") !== -1 ||
        text.indexOf("damaging winds") !== -1 ||
        text.indexOf("gusts") !== -1 ||
        text.indexOf("high profile vehicles") !== -1 ||
        /gusts?\s+(up to\s+)?\d{2}\s*mph/.test(text)
      ) {
        hasHighWinds = true;
      }

      if (event.indexOf("special weather statement") !== -1) {
        if (
          text.indexOf("elevated fire danger") !== -1 ||
          text.indexOf("critical fire weather") !== -1 ||
          text.indexOf("wildfire") !== -1
        ) {
          hasFireWeather = true;
        }

        if (
          text.indexOf("strong winds") !== -1 ||
          text.indexOf("damaging winds") !== -1 ||
          text.indexOf("gusts") !== -1 ||
          text.indexOf("high profile vehicles") !== -1 ||
          /gusts?\s+(up to\s+)?\d{2}\s*mph/.test(text)
        ) {
          hasHighWinds = true;
        }

        if (
          text.indexOf("dense fog") !== -1 ||
          text.indexOf("fog") !== -1
        ) {
          hasFog = true;
        }
      }
    });

    const nextFlags = {
      hasBurnBan,
      hasWinterStorm,
      hasWinterWatch,
      hasWinterWarning,
      hasSevereStorm,
      hasFlooding,
      hasFog,
      hasFireWeather,
      hasHighWinds,
      hasExtremeCold,
      hasSurfHazard,
    };

    setFlags(nextFlags);
    setFlagsReady(true);
    console.log("[AlertsContext] final flags:", nextFlags);
  };

  const fetchAlerts = useCallback(async () => {
    const countyAtStart = selectedCounty;

    if (!countyAtStart) {
      if (countyAtStart === countyRef.current) {
        setAlerts([]);
        resetFlags();
      }
      return;
    }

    setIsLoading(true);
    setFlagsReady(false);

    try {
      const mapping = countyMapping || [];
      const selectedCountyData = mapping.find((c) => c.county === countyAtStart);

      if (!selectedCountyData) {
        // If the county is invalid or mapping is missing, only clear if still same county
        if (countyAtStart === countyRef.current) {
          setAlerts([]);
          resetFlags();
        }
        return;
      }

      // 1. NWS alerts
      const nwsRes = await fetch(
        "https://api.weather.gov/alerts/active?zone=" +
          selectedCountyData.nws_county_code
      );
      if (!nwsRes.ok) throw new Error("NWS network error");
      const nwsJson = await nwsRes.json();

      console.groupCollapsed("[AlertsContext] NWS raw alerts");
      console.log("Request URL:", nwsRes.url);
      console.log("Features:", nwsJson.features);
      console.groupEnd();

      const weatherAlerts = (nwsJson.features || []).map((feature) => {
        return {
          id: feature.id,
          type: "weather",
          title:
            (feature.properties && feature.properties.headline) ||
            "Weather alert",
          severity: "warning",
          source: "nws",
          raw: feature,
          eventSubtypeSeverity: getSubtypeSeverityRank(feature),
        };
      });

      // 2. Burn ban
      const burnRes = await fetch(
        "https://tfsgis.tfs.tamu.edu/arcgis/rest/services/BurnBan/BurnBan/MapServer/0/query?f=json&where=County%20%3D%20'" +
          countyAtStart +
          "'%20AND%20BurnBan%20%3D%20'Yes'&returnGeometry=false&outFields=County,BurnBan"
      );
      if (!burnRes.ok) throw new Error("Burn ban network error");
      const burnJson = await burnRes.json();
      const hasBurnBan =
        burnJson.features && burnJson.features.length > 0;

      const burnAlerts = hasBurnBan
        ? [
            {
              id: "burnban-" + countyAtStart,
              type: "burnBan",
              title: "Burn ban in effect",
              severity: "warning",
              source: "tfs",
              raw: burnJson,
            },
          ]
        : [];

      const nextAlerts = weatherAlerts.concat(burnAlerts);

      // 3. Merge wildfire alerts (from client-side ESRI hook)
      const mergedAlerts = nextAlerts.concat(wildfireAlerts);

      // Only apply if we’re still on the same county (live value)
      if (countyAtStart === countyRef.current) {
        setAlerts(mergedAlerts);
        updateDerivedFlags(mergedAlerts);
      }
    } catch (e) {
      if (countyAtStart === countyRef.current) {
        console.error("Error fetching alerts:", e);
        setAlerts([]);
        resetFlags();
      }
    } finally {
      if (countyAtStart === countyRef.current) {
        setIsLoading(false);
      }
    }
  }, [selectedCounty, countyMapping, wildfireAlerts]);

  // Refetch whenever core inputs change
  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // Stable polling interval
  useEffect(() => {
    const id = setInterval(() => {
      fetchAlerts();
    }, 900000);
    return () => clearInterval(id);
  }, [fetchAlerts]);

  useEffect(() => {
    console.log("[AlertsContext] flagsReady changed:", flagsReady);
  }, [flagsReady]);

  return (
    <AlertsContext.Provider
      value={{
        alerts,
        isLoading,
        refreshAlerts: fetchAlerts,
        flags,
        flagsReady,
        setWildfireAlerts, // NEW
      }}
    >
      {children}
    </AlertsContext.Provider>
  );
};

export const useAlerts = () => {
  const ctx = useContext(AlertsContext);
  if (!ctx) {
    throw new Error("useAlerts must be used within AlertsProvider");
  }
  return ctx;
};
